/*
 * WorkAndBuhin.vue の部品数量増減と listSelectedBuhin/manualBuhinList への同期を切り出した処理。
 */
import { BuhinDetail } from "@/model/Buhin";
import {
  findJunseiBuhinIndex,
  findManualBuhinIndex,
  findSelectedBuhinIndex,
  isJunseiBuhinCode
} from "@/components/work-buhin/WorkBuhinMatch";

export interface ManualBuhinClickActionState {
  shouldToggleQuantity: boolean;
  shouldSyncJunseiBuhinSelection: boolean;
  shouldCheckDisabledBtnConfirm: boolean;
}

export interface ManualBuhinClickApplyEffectState {
  shouldToggleQuantity: boolean;
  shouldSyncJunseiBuhinSelection: boolean;
  shouldCheckDisabledBtnConfirm: boolean;
}

export interface NormalBuhinClickActionState {
  shouldPreventDefault: boolean;
  shouldUpdateQuantity: boolean;
  quantity: number;
  shouldSyncSelection: boolean;
  shouldUpdateRecommendBuhinSelected: boolean;
  shouldCheckDisabledBtnConfirm: boolean;
}

export type NormalBuhinClickApplyEffectState = NormalBuhinClickActionState;

export interface NormalBuhinQuantityInputActionState {
  shouldSyncSelection: boolean;
  shouldUpdateRecommendBuhinSelected: boolean;
  shouldCheckDisabledBtnConfirm: boolean;
}

export interface NormalBuhinQuantityInputApplyEffectState {
  shouldSyncSelection: boolean;
  shouldUpdateRecommendBuhinSelected: boolean;
  shouldCheckDisabledBtnConfirm: boolean;
}

export interface ManualBuhinQuantityInputActionState {
  shouldSyncSelection: boolean;
  shouldCheckDisabledBtnConfirm: boolean;
}

export interface ManualBuhinQuantityInputApplyEffectState {
  shouldSyncSelection: boolean;
  shouldCheckDisabledBtnConfirm: boolean;
}

export interface BuhinInputReadonlyState {
  isReadonly: boolean;
}

export interface BuhinQuantityControlState {
  isReadonly: boolean;
  isMinusDisabled: boolean;
  isPlusDisabled: boolean;
}

export interface BuhinQuantityPlusApplyEffectState {
  shouldUpdateNormalBuhinContent: boolean;
  shouldSyncNormalBuhinSelection: boolean;
  shouldUpdateRecommendBuhinSelected: boolean;
  shouldSyncManualOrJunseiBuhinSelection: boolean;
  shouldSyncManualBuhinListQuantity: boolean;
  shouldCheckDisabledBtnConfirm: boolean;
}

export interface BuhinQuantityMinusApplyEffectState {
  shouldUpdateNormalBuhinContent: boolean;
  shouldSyncNormalBuhinSelection: boolean;
  shouldUpdateRecommendBuhinSelectedWhenFound: boolean;
  shouldSyncManualOrJunseiBuhinSelection: boolean;
  shouldCheckDisabledBtnConfirm: boolean;
}

/*
 * increaseBuhinQuantity を WorkAndBuhin.vue から分離し、
 * 数量変更と選択同期 を親側置換や単体テストで固定するため。
 */
export function increaseBuhinQuantity(buhin: BuhinDetail): number {
  const step = Number(buhin.quantityStep) || 1;
  if (buhin.quantity == 0 && buhin.defaultQuantity) {
    buhin.quantity = buhin.defaultQuantity;
  } else {
    buhin.quantity = Number((+buhin.quantity + step).toFixed(1));
  }
  return buhin.quantity;
}

/*
 * decreaseBuhinQuantity を WorkAndBuhin.vue から分離し、
 * 数量変更と選択同期 を親側置換や単体テストで固定するため。
 */
export function decreaseBuhinQuantity(buhin: BuhinDetail): number {
  const step = Number(buhin.quantityStep) || 1;
  buhin.quantity = Number((buhin.quantity - step).toFixed(1));
  return buhin.quantity;
}

/*
 * syncNormalBuhinSelection を WorkAndBuhin.vue から分離し、
 * 数量変更と選択同期 を親側置換や単体テストで固定するため。
 */
export function syncNormalBuhinSelection(
  selectedBuhinList: BuhinDetail[],
  buhin: BuhinDetail
): boolean {
  const itemIndex = findSelectedBuhinIndex(selectedBuhinList, buhin);
  if (!buhin.quantity) {
    if (itemIndex > -1) {
      selectedBuhinList.splice(itemIndex, 1);
    }
    return false;
  }
  if (itemIndex == -1) {
    selectedBuhinList.push(buhin);
  } else {
    selectedBuhinList[itemIndex].quantity = buhin.quantity;
  }
  return true;
}

/*
 * syncNormalBuhinSelectionAfterDecrease を WorkAndBuhin.vue から分離し、
 * 数量変更と選択同期 を親側置換や単体テストで固定するため。
 */
export function syncNormalBuhinSelectionAfterDecrease(
  selectedBuhinList: BuhinDetail[],
  buhin: BuhinDetail
): { found: boolean; isSelected: boolean } {
  const itemIndex = findSelectedBuhinIndex(selectedBuhinList, buhin);
  if (itemIndex === -1) {
    return { found: false, isSelected: false };
  }
  if (buhin.quantity == 0) {
    selectedBuhinList.splice(itemIndex, 1);
    return { found: true, isSelected: false };
  }
  selectedBuhinList[itemIndex].quantity = buhin.quantity;
  return { found: true, isSelected: true };
}

/*
 * syncManualOrJunseiBuhinSelection を WorkAndBuhin.vue から分離し、
 * 数量変更と選択同期 を親側置換や単体テストで固定するため。
 */
export function syncManualOrJunseiBuhinSelection(
  selectedBuhinList: BuhinDetail[],
  buhin: BuhinDetail
): void {
  const itemIndex = findManualOrJunseiBuhinIndex(selectedBuhinList, buhin);
  if (!buhin.quantity) {
    if (itemIndex > -1) {
      selectedBuhinList.splice(itemIndex, 1);
    }
    return;
  }
  if (itemIndex == -1) {
    selectedBuhinList.push(buhin);
  } else {
    selectedBuhinList[itemIndex].quantity = buhin.quantity;
  }
}

/*
 * syncJunseiBuhinSelection を WorkAndBuhin.vue から分離し、
 * 数量変更と選択同期 を親側置換や単体テストで固定するため。
 */
export function syncJunseiBuhinSelection(
  selectedBuhinList: BuhinDetail[],
  buhin: BuhinDetail
): void {
  const junseiIndex = findJunseiBuhinIndex(selectedBuhinList, buhin);
  if (junseiIndex === -1 && buhin.quantity > 0) {
    selectedBuhinList.push(buhin);
  } else if (junseiIndex > -1) {
    if (buhin.quantity > 0) {
      selectedBuhinList[junseiIndex].quantity = buhin.quantity;
    } else {
      selectedBuhinList.splice(junseiIndex, 1);
    }
  }
}

/*
 * toggleManualBuhinQuantity を WorkAndBuhin.vue から分離し、
 * 数量変更と選択同期 を親側置換や単体テストで固定するため。
 */
export function toggleManualBuhinQuantity(buhin: BuhinDetail): number {
  if (buhin.quantity == 0) {
    if (buhin.quantityStep && buhin.quantityStep > 0) {
      buhin.quantity = buhin.quantityStep;
    } else {
      buhin.quantity = 1;
    }
  } else {
    buhin.quantity = 0;
  }
  return buhin.quantity;
}

/*
 * buildNormalBuhinClickActionState を WorkAndBuhin.vue から分離し、
 * 通常部品クリック時の disabled 分岐、数量初期値、選択同期の実行条件を固定するため。
 */
export function buildNormalBuhinClickActionState(params: {
  disabledBtnNoBuhin: boolean;
  isChecked: boolean;
  defaultQuantity?: number | null;
}): NormalBuhinClickActionState {
  if (params.disabledBtnNoBuhin) {
    return {
      shouldPreventDefault: true,
      shouldUpdateQuantity: false,
      quantity: 0,
      shouldSyncSelection: false,
      shouldUpdateRecommendBuhinSelected: false,
      shouldCheckDisabledBtnConfirm: false
    };
  }
  return {
    shouldPreventDefault: false,
    shouldUpdateQuantity: true,
    quantity: params.isChecked ? params.defaultQuantity || 1 : 0,
    shouldSyncSelection: true,
    shouldUpdateRecommendBuhinSelected: true,
    shouldCheckDisabledBtnConfirm: true
  };
}

/*
 * buildNormalBuhinClickApplyEffectState を WorkAndBuhin.vue から分離し、
 * 通常部品クリック後の preventDefault、数量反映、選択同期、確定ボタン再判定条件を固定するため。
 */
export function buildNormalBuhinClickApplyEffectState(
  actionState: NormalBuhinClickActionState
): NormalBuhinClickApplyEffectState {
  return {
    ...actionState
  };
}

/*
 * buildNormalBuhinQuantityInputActionState を WorkAndBuhin.vue から分離し、
 * 通常部品の数量入力後に選択同期とおすすめ部品表示更新を必ず通す条件を固定するため。
 */
export function buildNormalBuhinQuantityInputActionState(): NormalBuhinQuantityInputActionState {
  return {
    shouldSyncSelection: true,
    shouldUpdateRecommendBuhinSelected: true,
    shouldCheckDisabledBtnConfirm: true
  };
}

/*
 * buildNormalBuhinQuantityInputApplyEffectState を WorkAndBuhin.vue から分離し、
 * 通常部品数量入力後の選択同期、おすすめ表示更新、確定ボタン再判定条件を固定するため。
 */
export function buildNormalBuhinQuantityInputApplyEffectState(
  actionState: NormalBuhinQuantityInputActionState
): NormalBuhinQuantityInputApplyEffectState {
  return {
    shouldSyncSelection: actionState.shouldSyncSelection,
    shouldUpdateRecommendBuhinSelected: actionState.shouldUpdateRecommendBuhinSelected,
    shouldCheckDisabledBtnConfirm: actionState.shouldCheckDisabledBtnConfirm
  };
}

/*
 * buildManualBuhinQuantityInputActionState を WorkAndBuhin.vue から分離し、
 * 手入力・純正部品の数量入力後に選択同期と確定ボタン再判定を通す条件を固定するため。
 */
export function buildManualBuhinQuantityInputActionState(): ManualBuhinQuantityInputActionState {
  return {
    shouldSyncSelection: true,
    shouldCheckDisabledBtnConfirm: true
  };
}

/*
 * buildManualBuhinQuantityInputApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力・純正部品数量入力後の選択同期と確定ボタン再判定条件を固定するため。
 */
export function buildManualBuhinQuantityInputApplyEffectState(
  actionState: ManualBuhinQuantityInputActionState
): ManualBuhinQuantityInputApplyEffectState {
  return {
    shouldSyncSelection: actionState.shouldSyncSelection,
    shouldCheckDisabledBtnConfirm: actionState.shouldCheckDisabledBtnConfirm
  };
}

/*
 * buildBuhinInputReadonlyState を WorkAndBuhin.vue から分離し、
 * 部品単価や作業工賃の入力可否が部品なし状態に連動する条件を固定するため。
 */
export function buildBuhinInputReadonlyState(params: {
  disabledBtnNoBuhin: boolean;
}): BuhinInputReadonlyState {
  return {
    isReadonly: params.disabledBtnNoBuhin
  };
}

/*
 * buildBuhinQuantityControlState を WorkAndBuhin.vue から分離し、
 * 数量入力 readonly と +/- ボタン disabled の境界条件を同じ state で固定するため。
 */
export function buildBuhinQuantityControlState(params: {
  disabledBtnNoBuhin: boolean;
  quantity: any;
}): BuhinQuantityControlState {
  const quantity = Number(params.quantity);
  return {
    isReadonly: params.disabledBtnNoBuhin,
    isMinusDisabled: params.disabledBtnNoBuhin || quantity <= 0,
    isPlusDisabled: params.disabledBtnNoBuhin || quantity >= 9
  };
}

/*
 * buildBuhinQuantityPlusApplyEffectState を WorkAndBuhin.vue から分離し、
 * + ボタン押下後に通常部品と手入力・純正部品で実行する選択同期、表示更新、確定ボタン再判定条件を固定するため。
 */
export function buildBuhinQuantityPlusApplyEffectState(params: {
  isNormalBuhin: boolean;
}): BuhinQuantityPlusApplyEffectState {
  return {
    shouldUpdateNormalBuhinContent: params.isNormalBuhin,
    shouldSyncNormalBuhinSelection: params.isNormalBuhin,
    shouldUpdateRecommendBuhinSelected: params.isNormalBuhin,
    shouldSyncManualOrJunseiBuhinSelection: !params.isNormalBuhin,
    shouldSyncManualBuhinListQuantity: !params.isNormalBuhin,
    shouldCheckDisabledBtnConfirm: true
  };
}

/*
 * buildBuhinQuantityMinusApplyEffectState を WorkAndBuhin.vue から分離し、
 * - ボタン押下後に通常部品と手入力・純正部品で実行する選択同期、表示更新、確定ボタン再判定条件を固定するため。
 */
export function buildBuhinQuantityMinusApplyEffectState(params: {
  isNormalBuhin: boolean;
}): BuhinQuantityMinusApplyEffectState {
  return {
    shouldUpdateNormalBuhinContent: params.isNormalBuhin,
    shouldSyncNormalBuhinSelection: params.isNormalBuhin,
    shouldUpdateRecommendBuhinSelectedWhenFound: params.isNormalBuhin,
    shouldSyncManualOrJunseiBuhinSelection: !params.isNormalBuhin,
    shouldCheckDisabledBtnConfirm: true
  };
}

/*
 * buildManualBuhinClickActionState を WorkAndBuhin.vue から分離し、
 * 手入力部品クリック後の数量 toggle と純正部品同期の実行条件を親側置換や単体テストで固定するため。
 */
export function buildManualBuhinClickActionState(params: {
  disabledBtnNoBuhin: boolean;
  buhinCd: string | null | undefined;
}): ManualBuhinClickActionState {
  if (params.disabledBtnNoBuhin) {
    return {
      shouldToggleQuantity: false,
      shouldSyncJunseiBuhinSelection: false,
      shouldCheckDisabledBtnConfirm: false
    };
  }
  return {
    shouldToggleQuantity: true,
    shouldSyncJunseiBuhinSelection: isJunseiBuhinCode(params.buhinCd),
    shouldCheckDisabledBtnConfirm: true
  };
}

/*
 * buildManualBuhinClickApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力部品クリック後の数量 toggle、純正部品同期、confirm button 再判定の副作用条件を固定するため。
 */
export function buildManualBuhinClickApplyEffectState(
  actionState: ManualBuhinClickActionState
): ManualBuhinClickApplyEffectState {
  return {
    shouldToggleQuantity: actionState.shouldToggleQuantity,
    shouldSyncJunseiBuhinSelection: actionState.shouldSyncJunseiBuhinSelection,
    shouldCheckDisabledBtnConfirm: actionState.shouldCheckDisabledBtnConfirm
  };
}

/*
 * syncManualBuhinListQuantityOnPlus を WorkAndBuhin.vue から分離し、
 * 数量変更と選択同期 を親側置換や単体テストで固定するため。
 */
export function syncManualBuhinListQuantityOnPlus(
  manualBuhinList: BuhinDetail[],
  buhin: BuhinDetail
): void {
  const isJunseiBuhin = isJunseiBuhinCode(buhin.buhinCd);
  const foundBuhin = manualBuhinList.find(
    (item: any) =>
      item.buhinCd == buhin.buhinCd &&
      item.buhinId == buhin.buhinId &&
      (isJunseiBuhin && item.buhinNm == buhin.buhinNm)
  );
  if (foundBuhin != null) {
    foundBuhin.quantity = buhin.quantity;
  }
}

function findManualOrJunseiBuhinIndex(
  selectedBuhinList: BuhinDetail[],
  buhin: BuhinDetail
): number {
  if (isJunseiBuhinCode(buhin.buhinCd)) {
    return findJunseiBuhinIndex(selectedBuhinList, buhin);
  }
  return findManualBuhinIndex(selectedBuhinList, buhin);
}

// 増減幅、部品個数、マスタ初期個数のいずれかが小数なら、小数入力を許可する。
export function getQuantityDecimalPlaces(buhin: BuhinDetail): number {
  const step = Number(buhin.quantityStep) || 1;
  const quantity = Number(buhin.quantity) || 0;
  const defaultQuantity = Number(buhin.defaultQuantity) || 0;
  return step % 1 !== 0 || quantity % 1 !== 0 || defaultQuantity % 1 !== 0 ? 1 : 0;
}
